import { useEffect, useRef } from "react";
import { authService } from "@/services";
import { accountApi } from "@/resources/account";

const REFRESH_THROTTLE_MS = 5 * 60 * 1000;
// mousemove deliberately excluded -- unlike every other event here, it
// fires from purely passive/incidental motion (trackpad drift, a mouse
// resting on an uneven surface, desk vibration) with zero user intent.
// QA found a real session outliving its 1h TTL by 15-30+ min with no
// deliberate interaction (AUT-125) -- traced to mousemove satisfying this
// list and silently re-issuing a fresh-TTL token via /refresh. Every event
// still listed here requires an actual deliberate action to fire.
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "click"] as const;

// AUT-125: without "remember me" the token now expires in 1 hour
// (AuthController::login) instead of the old 24h default -- a real
// server-side cutoff for a genuinely idle session. Left alone, that would
// also log out someone actively using the app for over an hour, so this
// silently renews the token via the (previously unused) /refresh endpoint
// whenever there's been real interaction, throttled so it doesn't fire on
// every click. If activity stops, no more refreshes happen and the token
// expires on schedule -- the existing 401 interceptor handles the rest.
//
// Only listens for genuine user interaction, never the app's own
// background polling (notifications, etc.) -- refreshing off automatic
// requests would keep an abandoned-but-open tab logged in forever.
export function useKeepSessionAlive() {
  const { mutate: refresh } = accountApi.useRefreshToken();
  const lastRefreshRef = useRef(Date.now());
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (authService.isSessionRemembered()) return;

    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastRefreshRef.current > REFRESH_THROTTLE_MS) {
        lastRefreshRef.current = now;
        refreshRef.current();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, maybeRefresh, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, maybeRefresh));
    };
  }, []);
}
